import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

const StudentSelect = ({ 
  students, 
  selectedStudent, 
  handleStudentChange, 
  getFullName 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const containerRef = useRef(null);

  // Filter students based on search term
  const filteredStudents = useMemo(() => {
    if (!searchTerm) return [];

    return students
      .filter(student => {
        // Check if student is already selected
        const isSelected = selectedStudents.some(s => s._id === student._id);
        if (isSelected) return false;
        
        // Filter by name or admission number
        return getFullName(student).toLowerCase().includes(searchTerm.toLowerCase()) || 
               (student.admissionNumber && student.admissionNumber.toLowerCase().includes(searchTerm.toLowerCase()));
      })
      .slice(0, 5); // Limit to 5 suggestions
  }, [students, searchTerm, getFullName, selectedStudents]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle student selection
  const handleSelect = (student) => {
    const updatedSelection = [...selectedStudents, student];
    setSelectedStudents(updatedSelection);
    
    // Create comma-separated IDs for the backend
    const studentIds = updatedSelection.map(s => s._id).join(',');
    handleStudentChange(studentIds);
    
    setSearchTerm('');
    setIsOpen(false);
  };

  // Remove a student from selection
  const removeStudent = (studentId) => {
    const updatedSelection = selectedStudents.filter(s => s._id !== studentId);
    setSelectedStudents(updatedSelection);
    
    // Update the comma-separated IDs
    const studentIds = updatedSelection.map(s => s._id).join(',');
    handleStudentChange(studentIds || ''); // Send empty string if no students selected
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
  };

  // Initialize selected students from props if needed
  useEffect(() => {
    // Clear selected students if selectedStudent prop is empty
    if (!selectedStudent || selectedStudent.trim() === '') {
      setSelectedStudents([]);
      return;
    }

    if (selectedStudent && selectedStudent.includes(',')) {
      // Handle comma-separated IDs
      const ids = selectedStudent.split(',').filter(id => id.trim());
      const selected = ids.map(id => 
        students.find(s => s._id === id.trim())
      ).filter(Boolean);
      
        setSelectedStudents(selected);
    } else if (selectedStudent) {
      // Handle single ID
      const student = students.find(s => s._id === selectedStudent);
      if (student) {
        setSelectedStudents([student]);
      } else {
        // If student not found, clear selection
        setSelectedStudents([]);
      }
    }
  }, [selectedStudent, students]);

  return (
    <div className="w-full space-y-2">
      <label className="block text-sm font-medium mb-2">
        Select Students
      </label>
      
      {/* Selected students display */}
      {selectedStudents.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedStudents.map(student => (
            <Badge key={student._id} variant="secondary" className="flex items-center gap-1 px-3 py-1">
              {getFullName(student)}
              {student.admissionNumber && (
                <span className="text-xs text-gray-500 ml-1">
                  ({student.admissionNumber})
                </span>
              )}
              <button 
                onClick={() => removeStudent(student._id)}
                className="ml-1 rounded-full hover:bg-gray-200 p-0.5"
              >
                <X size={14} />
              </button>
            </Badge>
          ))}
        </div>
      )}
      
      {/* Search input */}
      <div className="relative w-full" ref={containerRef}>
        <Input 
          type="text" 
          placeholder="Search students by name or admission number..." 
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => setIsOpen(true)}
          className="w-full"
        />
        
        {isOpen && filteredStudents.length > 0 && (
          <div className="absolute z-50 top-full mt-1 w-full border rounded-md shadow-lg bg-white max-h-60 overflow-y-auto">
            {filteredStudents.map((student) => (
              <div 
                key={student._id}
                onClick={() => handleSelect(student)}
                className="cursor-pointer hover:bg-gray-100 p-2 border-b last:border-b-0"
              >
                <div>
                  <span className="font-medium">{getFullName(student)}</span>
                  {student.admissionNumber && (
                    <span className="text-sm text-gray-500 block">
                      Admission #: {student.admissionNumber}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {isOpen && searchTerm && filteredStudents.length === 0 && (
          <div className="absolute z-50 top-full mt-1 w-full border rounded-md shadow-lg bg-white p-2 text-center text-gray-500">
            No matching students found
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSelect;
